import { Link } from 'react-router-dom';
import CertificationLayout from '../../layouts/CertificationLayout';
import { AffiliateCTA } from '../../components/content/AffiliateCTA';
import { ResourceLink } from '../../components/content/ResourceLink';
import { CalculatorEmbed } from '../../components/content/CalculatorEmbed';
import type { ExamDetails } from '../../layouts/CertificationLayout';
import type { TocItem, RelatedCalculator } from '../../layouts/GuideLayout';

const examDetails: ExamDetails = {
  examCode: 'AZ-500',
  examName: 'Microsoft Azure Security Engineer',
  passingScore: 700,
  examCostGBP: 165,
  retirementDate: '31 August 2026',
  skillsMeasured: [
    'Manage identity and access (20–25%)',
    'Secure networking (25–30%)',
    'Secure compute, storage, and databases (15–20%)',
    'Manage security operations (30–35%)',
  ],
};

const tocItems: TocItem[] = [
  { id: 'what-is-az500', label: 'What is AZ-500?', level: 2 },
  { id: 'exam-topics', label: 'Exam Topic Breakdown', level: 2 },
  { id: 'uk-context', label: 'UK Context', level: 2 },
  { id: 'az500-vs-sc200', label: 'AZ-500 vs SC-200', level: 2 },
  { id: 'free-resources', label: 'Free Study Resources', level: 2 },
  { id: 'paid-resources', label: 'Paid Study Resources', level: 2 },
  { id: 'study-plan', label: 'Recommended Study Plan', level: 2 },
  { id: 'labs-warning', label: 'Exam Labs Warning', level: 2 },
  { id: 'related-resources', label: 'Related Resources', level: 2 },
];

const relatedCalculators: RelatedCalculator[] = [
  { service: 'log-analytics', path: '/log-analytics', label: 'Log Analytics & Sentinel Calculator' },
];

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'AZ-500 Study Guide UK 2026',
  description:
    'Complete AZ-500 study guide for UK engineers. Note: retires August 2026.',
  author: { '@type': 'Organization', name: 'AzureCalc.uk' },
  publisher: {
    '@type': 'Organization',
    name: 'AzureCalc.uk',
    url: 'https://www.azure-calc.co.uk',
  },
  dateModified: '2026-03-30',
  url: 'https://www.azure-calc.co.uk/certifications/az-500-study-guide-uk',
};

const topCta = (
  <AffiliateCTA
    programme="Pluralsight"
    courseName="AZ-500 Microsoft Azure Security Technologies"
    url="https://www.pluralsight.com/paths/az-500-microsoft-azure-security-engineer-associate"
    price="From £29/month"
    badgeText="Most Comprehensive"
  />
);

export default function AZ500StudyGuidePage() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <CertificationLayout
        title="AZ-500 Study Guide UK 2026 — Microsoft Azure Security Engineer"
        description="Complete AZ-500 study guide for UK engineers. Exam structure, all topic domains, free and paid resources. Note: AZ-500 retires August 2026 — consider SC-200 as the successor. Updated March 2026."
        lastUpdated="March 2026"
        tocItems={tocItems}
        examDetails={examDetails}
        relatedCalculators={relatedCalculators}
        topCta={topCta}
        bottomCta={
          <AffiliateCTA
            programme="MeasureUp"
            courseName="AZ-500 Official Practice Test"
            url="https://www.measureup.com/microsoft-practice-test-az-500-microsoft-azure-security-technologies.html"
            price="From £99"
            badgeText="Official Microsoft Partner"
          />
        }
        canonical="https://www.azure-calc.co.uk/certifications/az-500-study-guide-uk"
        metaTitle="AZ-500 Study Guide UK 2026 | AzureCalc.uk"
        metaDescription="Complete AZ-500 Azure Security Engineer study guide for UK engineers. Note: retires August 2026. Exam domains, study plan, free and paid resources."
      >
        {/* ── Retirement Warning ── */}
        <div className="border-2 rounded-lg p-5 mb-8 bg-[#e8b000]/10 border-[#e8b000]/50">
          <div className="flex items-start gap-3">
            <span
              className="material-symbols-outlined text-[#e8b000] text-[26px] shrink-0"
              style={{ fontVariationSettings: "'FILL' 1" }}
            >
              warning
            </span>
            <div>
              <p className="text-[14px] font-bold text-[#e8b000] mb-2">
                AZ-500 retires on 31 August 2026
              </p>
              <p className="text-[13px] text-[#c8a84b]/90 leading-relaxed mb-3">
                Microsoft has announced this exam will be retired. If you are planning to sit
                AZ-500, book your exam before August 2026. Certifications earned before retirement
                remain valid for two years from the date you pass.
              </p>
              <p className="text-[13px] text-[#c8a84b]/90 leading-relaxed">
                After retirement, consider{' '}
                <Link
                  to="/certifications/sc-200-study-guide-uk"
                  className="text-[#e8b000] underline hover:text-white transition-colors"
                >
                  SC-200 (Security Operations Analyst)
                </Link>{' '}
                as the nearest successor for security roles — it has no announced retirement date
                and covers Microsoft Sentinel and Defender XDR in depth.
              </p>
            </div>
          </div>
        </div>

        {/* ── Section 1: What is AZ-500? ── */}
        <section id="what-is-az500" className="mb-10">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>What is AZ-500?</h2>
          <p className="text-[15px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            The AZ-500 — Microsoft Azure Security Engineer Associate — validates your ability to
            implement and manage security controls across Azure infrastructure. This includes
            securing identities with Microsoft Entra ID, hardening Azure networks with firewalls
            and private endpoints, encrypting compute and storage resources, and operating security
            tooling like Microsoft Defender for Cloud and Azure Sentinel. It is a role-based
            certification aimed at cloud security engineers, security architects, and Azure
            administrators moving into security-focused roles.
          </p>
          <p className="text-[15px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            The key distinction between AZ-500 and SC-200 is the direction of the work.{' '}
            <strong style={{ color: 'var(--text-primary)' }}>AZ-500 is about building the secure environment</strong>{' '}
            — implementing Conditional Access, configuring Key Vault, deploying Azure Firewall,
            designing RBAC. <strong style={{ color: 'var(--text-primary)' }}>SC-200 is about operating within
            that environment</strong> — detecting threats, investigating incidents, responding to
            alerts in Microsoft Sentinel. Most mature security teams need both skill sets; many
            engineers hold both certifications. If you are choosing between them, your current
            day-to-day role is the best guide.
          </p>
          <p className="text-[15px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            AZ-104 (Azure Administrator) is strongly recommended as a prerequisite — AZ-500
            assumes familiarity with Azure resource management, networking fundamentals, and
            identity concepts at an administrator level. The exam contains 40–60 questions
            across multiple formats, including multiple choice, case studies, and
            performance-based lab tasks in a live Azure environment. Passing score is 700 out of
            1000, and the exam costs £165 via Pearson VUE.
          </p>
        </section>

        {/* ── Section 2: Exam Topic Breakdown ── */}
        <section id="exam-topics" className="mb-10">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Exam Topic Breakdown</h2>
          <p className="text-[14px] leading-relaxed mb-6" style={{ color: 'var(--text-tertiary)' }}>
            AZ-500 covers four domains. Weightings are from Microsoft's official study guide and
            are approximate — your exam may vary within these ranges.
          </p>

          <div className="flex flex-col gap-4">
            {[
              {
                num: 1,
                title: 'Manage identity and access',
                weight: '20–25%',
                items: [
                  'Microsoft Entra ID (formerly Azure AD) — users, groups, roles',
                  'Managed identities and service principals for Azure resources',
                  'Conditional Access policies — MFA, device compliance, named locations',
                  'Privileged Identity Management (PIM) — just-in-time access, approval workflows',
                  'External identities and B2B collaboration settings',
                ],
              },
              {
                num: 2,
                title: 'Secure networking',
                weight: '25–30%',
                items: [
                  'Azure Firewall and Firewall Manager — DNAT, application, and network rules',
                  'Network Security Groups (NSGs) and Application Security Groups (ASGs)',
                  'Azure DDoS Protection — Standard plan, mitigation policies',
                  'Private endpoints and service endpoints — locking down PaaS services',
                  'VPN Gateway and ExpressRoute security configurations',
                  'Azure Bastion — secure RDP/SSH without public IP exposure',
                ],
              },
              {
                num: 3,
                title: 'Secure compute, storage, and databases',
                weight: '15–20%',
                items: [
                  'VM security — just-in-time VM access, endpoint protection, disk encryption',
                  'Container and AKS security — pod identity, network policies, image scanning',
                  'Azure Key Vault — secrets, certificates, key management, access policies',
                  'Storage account security — SAS tokens, access keys, customer-managed keys',
                  'Database security — SQL auditing, Always Encrypted, row-level security',
                ],
              },
              {
                num: 4,
                title: 'Manage security operations',
                weight: '30–35%',
                items: [
                  'Microsoft Defender for Cloud — CSPM, Secure Score, workload protection',
                  'Log Analytics and Azure Monitor — workspace configuration, diagnostics',
                  'Microsoft Sentinel basics — workspace setup, data connectors, simple analytics rules',
                  'Security policies and initiatives — Azure Policy, regulatory compliance',
                  'Vulnerability assessment — Defender for Servers integration, recommendations',
                ],
                note: 'Domain 4 overlaps significantly with SC-200 content. Engineers preparing for both certifications will find substantial shared material here.',
              },
            ].map(({ num, title, weight, items, note }) => (
              <div
                key={num}
                className="rounded-lg p-5"
                style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[16px] font-bold" style={{ color: '#c7e0f4' }}>
                    Domain {num}: {title}
                  </h3>
                  <span className="shrink-0 text-[12px] font-bold text-[#0078d4] px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid #0078d4' }}>
                    {weight}
                  </span>
                </div>
                <ul className="flex flex-col gap-1.5 mb-3">
                  {items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                      <span className="text-[#0078d4] shrink-0 mt-0.5">›</span>
                      {item}
                    </li>
                  ))}
                </ul>
                {note && (
                  <div className="flex items-start gap-2 rounded p-3 mt-2" style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid rgba(0, 120, 212, 0.3)' }}>
                    <span className="material-symbols-outlined text-[#0078d4] text-[14px] shrink-0 mt-0.5">info</span>
                    <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>{note}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* ── Section 3: UK Context ── */}
        <section id="uk-context" className="mb-10">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>UK Context</h2>
          <p className="text-[15px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            AZ-500 is well-regarded in the UK job market for senior cloud security roles.
            Typical salary ranges for UK engineers holding AZ-500 are{' '}
            <strong style={{ color: 'var(--text-primary)' }}>£55,000–£85,000</strong>, with security
            architects and lead engineers in London-based financial services firms regularly
            exceeding £90,000. The certification has strong recognition in UK financial services,
            defence, and central government — sectors that have adopted Azure heavily and face
            significant regulatory pressure to demonstrate cloud security competence.
          </p>
          <p className="text-[15px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            In UK government and defence, the{' '}
            <strong style={{ color: 'var(--text-primary)' }}>Defence Cyber Protection Partnership (DCPP)</strong>{' '}
            requires suppliers to demonstrate appropriate cyber security standards, and holding
            recognised cloud security certifications strengthens this case. Many UK organisations
            pursuing Cyber Essentials Plus or ISO 27001 certification also require their cloud
            security staff to hold formal credentials — AZ-500 is commonly listed alongside CISSP
            and CISM in UK job specifications for these roles.
          </p>

          <div className="rounded-lg p-5 flex flex-col gap-2" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
            <h3 className="text-[14px] font-bold mb-1" style={{ color: '#c7e0f4' }}>Practical information for UK candidates</h3>
            {[
              'Book via Pearson VUE at pearsonvue.com/microsoft — online proctored or in-person',
              'Test centres in London, Manchester, Birmingham, Edinburgh, and Bristol',
              'Exam is English-only — no Welsh language option currently available',
              'Most UK employers will fund AZ-500 as continuing professional development (CPD)',
              'Microsoft Exam Replay offers a discounted retake voucher purchasable at booking time',
              'Reschedule free up to 6 business days before — cancellation within 6 days forfeits the fee',
            ].map((item) => (
              <div key={item} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                <span className="text-[#0078d4] shrink-0 mt-0.5">›</span>
                {item}
              </div>
            ))}
          </div>
        </section>

        {/* ── Section 4: AZ-500 vs SC-200 ── */}
        <section id="az500-vs-sc200" className="mb-10">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>AZ-500 vs SC-200 — Which First?</h2>
          <p className="text-[15px] leading-relaxed mb-5" style={{ color: 'var(--text-secondary)' }}>
            These two certifications cover adjacent but distinct skill sets, and the right choice
            depends on your current role and career direction.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            {[
              {
                cert: 'Choose AZ-500 if',
                colour: 'border-[#0078d4]',
                iconColour: 'text-[#0078d4]',
                items: [
                  'You build and secure Azure infrastructure (security engineer role)',
                  'You already hold AZ-104 or work as an Azure administrator',
                  'You work in cloud architecture or DevSecOps',
                  'Your organisation needs someone to design and implement security controls',
                  'You need this cert before the August 2026 retirement date',
                ],
              },
              {
                cert: 'Choose SC-200 if',
                colour: 'border-[#107c10]',
                iconColour: 'text-[#107c10]',
                items: [
                  'You work in a SOC or threat hunting role',
                  'You use Microsoft Sentinel or Defender XDR daily',
                  'You are a security analyst rather than a security engineer',
                  'You want a certification with no announced retirement date',
                  'You are starting from scratch in Azure security',
                ],
              },
            ].map(({ cert, colour, iconColour, items }) => (
              <div
                key={cert}
                className={`border-l-2 ${colour} border-t border-r border-b rounded-lg p-5`}
                style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}
              >
                <h3 className="text-[15px] font-bold mb-3" style={{ color: '#c7e0f4' }}>{cert}</h3>
                <ul className="flex flex-col gap-1.5">
                  {items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                      <span className={`${iconColour} shrink-0 mt-0.5`}>›</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="rounded-lg p-4 flex items-start gap-3 bg-[#e8b000]/10 border border-[#e8b000]/40">
            <span className="material-symbols-outlined text-[#e8b000] text-[18px] shrink-0 mt-0.5">schedule</span>
            <div className="flex flex-col gap-1">
              <p className="text-[13px] font-semibold text-[#e8b000]">Retirement consideration</p>
              <p className="text-[13px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
                AZ-500 retires August 2026. SC-200 has no announced retirement date. For a
                long-term career investment, SC-200 is currently the stronger standalone choice.
                Many security engineers hold both — AZ-500 for the architecture and implementation
                depth, SC-200 for the operational and detection coverage.
              </p>
            </div>
          </div>
        </section>

        {/* ── Section 5: Free Resources ── */}
        <section id="free-resources" className="mb-10">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Free Study Resources</h2>
          <p className="text-[15px] leading-relaxed mb-5" style={{ color: 'var(--text-secondary)' }}>
            Microsoft provides strong free coverage for AZ-500 through Microsoft Learn. The main
            gap — as with all Microsoft certification free resources — is the lack of realistic
            practice exam questions and structured feedback on weak areas.
          </p>

          <div className="flex flex-col gap-3">
            {[
              {
                title: 'Official AZ-500 learning path on Microsoft Learn',
                url: 'https://learn.microsoft.com/en-us/training/paths/implement-resource-mgmt-security/',
                description: 'Covers all four exam domains with modules, knowledge checks, and sandbox labs. Start here.',
              },
              {
                title: 'AZ-500 study guide (official PDF from Microsoft)',
                url: 'https://learn.microsoft.com/en-us/credentials/certifications/resources/study-guides/az-500',
                description: 'Official list of every exam objective with links to corresponding Learn modules. Use as a revision checklist.',
              },
              {
                title: 'Azure security documentation',
                url: 'https://learn.microsoft.com/en-us/azure/security/',
                description: 'Deep reference documentation for Azure security features — Key Vault, Defender for Cloud, network security.',
              },
              {
                title: 'Azure security baseline documentation',
                url: 'https://learn.microsoft.com/en-us/security/benchmark/azure/',
                description: 'Microsoft Cloud Security Benchmark — covers security controls across all Azure services. Directly relevant to Domain 4.',
              },
              {
                title: "John Savill's AZ-500 YouTube series",
                url: 'https://www.youtube.com/@NTFAQGuy',
                description: "Widely regarded as the best free Azure certification resource. John Savill's AZ-500 coverage is comprehensive and explains the reasoning behind each security control.",
              },
            ].map((item) => (
              <div key={item.title} className="border rounded-lg p-4" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[14px] font-semibold text-[#0078d4] hover:underline transition-colors flex items-center gap-1.5 mb-1"
                >
                  {item.title}
                  <span className="material-symbols-outlined text-[14px]" style={{ color: 'var(--text-tertiary)' }}>open_in_new</span>
                </a>
                <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>{item.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Section 6: Paid Resources ── */}
        <section id="paid-resources" className="mb-10">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Paid Study Resources</h2>
          <p className="text-[15px] leading-relaxed mb-5" style={{ color: 'var(--text-secondary)' }}>
            For AZ-500, a video course plus an official practice test is the recommended
            combination. The exam includes hands-on lab tasks — make sure your study approach
            includes real Azure practice, not just video watching.
          </p>

          <div className="flex flex-col gap-4">
            <AffiliateCTA
              programme="Pluralsight"
              courseName="AZ-500 Microsoft Azure Security Technologies"
              url="https://www.pluralsight.com/paths/az-500-microsoft-azure-security-engineer-associate"
              price="From £29/month"
              badgeText="Most Comprehensive"
            />
            <div className="border rounded-lg p-4 text-[13px] leading-relaxed" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)', color: 'var(--text-tertiary)' }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Why Pluralsight for AZ-500:</strong> The learning
              path covers all four exam domains with hands-on labs that complement the theory
              modules. The identity and networking modules are particularly well structured.
              The AZ-500 content is regularly updated to reflect Microsoft's product changes —
              important given that Entra ID and Defender for Cloud evolve quickly.
            </div>

            <AffiliateCTA
              programme="MeasureUp"
              courseName="AZ-500 Official Practice Test"
              url="https://www.measureup.com/microsoft-practice-test-az-500-microsoft-azure-security-technologies.html"
              price="From £99"
              badgeText="Official Microsoft Partner"
            />
            <div className="border rounded-lg p-4 text-[13px] leading-relaxed" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)', color: 'var(--text-tertiary)' }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Why MeasureUp:</strong> Official Microsoft
              partner practice tests. The question format and difficulty closely match the real
              exam. Scoring 75%+ on a MeasureUp full test is a reliable exam-readiness signal.
              Essential for AZ-500 given the technical depth of the questions.
            </div>

            <AffiliateCTA
              programme="Whizlabs"
              courseName="AZ-500 Practice Tests"
              url="https://www.whizlabs.com/microsoft-azure-certification-az-500/"
              price="From £15"
              badgeText="Budget Option"
            />
            <div className="border rounded-lg p-4 text-[13px] leading-relaxed" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)', color: 'var(--text-tertiary)' }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Why Whizlabs:</strong> A large question bank
              at low cost, useful for reinforcing knowledge after completing your primary study
              materials. Best used as a supplement to MeasureUp rather than a replacement.
            </div>
          </div>
        </section>

        {/* ── Section 7: Study Plan ── */}
        <section id="study-plan" className="mb-10">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Recommended Study Plan</h2>
          <p className="text-[15px] leading-relaxed mb-5" style={{ color: 'var(--text-secondary)' }}>
            The following 8-week plan assumes part-time study of 1–2 hours per day. AZ-500
            requires more hands-on lab time than most Azure certifications — factor in time to
            work in a live Azure environment, not just watch videos.
          </p>

          <div className="flex flex-col gap-3">
            {[
              {
                week: 'Weeks 1–2',
                domain: 'Identity and Access (Domain 1)',
                colour: 'border-[#0078d4]',
                items: [
                  'Complete Microsoft Entra ID modules on Microsoft Learn',
                  'Practice PIM configuration — create eligible assignments, configure approval workflows',
                  'Build Conditional Access policies in a trial tenant — block legacy auth, require MFA',
                  'Understand the difference between RBAC, Azure AD roles, and PIM-managed roles',
                ],
              },
              {
                week: 'Weeks 3–4',
                domain: 'Secure Networking (Domain 2)',
                colour: 'border-[#0078d4]',
                items: [
                  'Deploy Azure Firewall in a hub-spoke topology in your lab subscription',
                  'Configure NSG flow logs and review traffic in Network Watcher',
                  'Create private endpoints for a Storage Account and SQL Database',
                  'Understand the difference between service endpoints and private endpoints — commonly tested',
                  'Deploy Azure Bastion and test RDP access without a public IP',
                ],
              },
              {
                week: 'Week 5',
                domain: 'Compute, Storage, and Databases (Domain 3)',
                colour: 'border-[#0078d4]',
                items: [
                  'Create an Azure Key Vault, add secrets and certificates, configure access policies',
                  'Practice storage SAS token types — account SAS vs service SAS vs stored access policy',
                  'Enable disk encryption with customer-managed keys (CMK) on a VM',
                  'Configure just-in-time VM access in Defender for Cloud',
                ],
              },
              {
                week: 'Weeks 6–7',
                domain: 'Security Operations (Domain 4)',
                colour: 'border-[#604878]',
                items: [
                  'Work through Defender for Cloud CSPM — review Secure Score and recommendations',
                  'Configure a Log Analytics workspace and connect Azure diagnostics',
                  'Set up a basic Sentinel workspace — add data connectors, create a scheduled analytics rule',
                  'This domain overlaps with SC-200 — if you plan to sit both, this is your shared study area',
                  'Review Azure Policy — create a custom policy definition and assign at subscription scope',
                ],
              },
              {
                week: 'Week 8',
                domain: 'Practice exams and lab tasks',
                colour: 'border-[#e8b000]',
                items: [
                  'Take a full MeasureUp practice test under timed conditions',
                  'Identify and review every incorrect answer before re-testing',
                  'Practice specific lab task types: configure PIM, deploy Key Vault, create custom RBAC roles',
                  'Review Microsoft Learn modules for your two lowest-scoring domains',
                  'Book the exam when consistently scoring 75%+ on practice tests',
                ],
              },
            ].map(({ week, domain, colour, items }) => (
              <div
                key={week}
                className={`border-l-2 ${colour} rounded-lg p-5`}
                style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
              >
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="text-[11px] font-bold uppercase tracking-widest shrink-0" style={{ color: 'var(--text-tertiary)' }}>{week}</span>
                  <h3 className="text-[15px] font-bold" style={{ color: '#c7e0f4' }}>{domain}</h3>
                </div>
                <ul className="flex flex-col gap-1.5">
                  {items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                      <span className="text-[#0078d4] shrink-0 mt-0.5">›</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* ── Section 8: Exam Labs Warning ── */}
        <section id="labs-warning" className="mb-10">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Exam Labs Warning</h2>

          <div className="border-2 rounded-lg p-5 mb-6 bg-[#e8b000]/10 border-[#e8b000]/50">
            <div className="flex items-start gap-3">
              <span
                className="material-symbols-outlined text-[#e8b000] text-[24px] shrink-0"
                style={{ fontVariationSettings: "'FILL' 1" }}
              >
                science
              </span>
              <div>
                <p className="text-[14px] font-bold text-[#e8b000] mb-2">
                  AZ-500 includes performance-based lab tasks
                </p>
                <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(200,168,75,0.9)' }}>
                  Unlike AZ-900 and SC-200, AZ-500 includes hands-on performance-based tasks in a
                  live Azure environment. You cannot prepare for these with multiple-choice practice
                  tests alone — you must have real hands-on experience in Azure before exam day.
                </p>
              </div>
            </div>
          </div>

          <p className="text-[15px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            Lab tasks in AZ-500 exams typically involve completing a real Azure configuration in a
            sandboxed subscription. Common tasks include: configuring Privileged Identity
            Management role assignments, deploying an Azure Key Vault and assigning access
            policies, setting up a Conditional Access policy, creating a custom RBAC role
            definition, and enabling Defender for Cloud on a subscription. You are given a set of
            instructions and must complete the tasks in the live portal — there are no multiple-
            choice options.
          </p>

          <div className="border rounded-lg p-5 flex flex-col gap-3" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
            <h3 className="text-[15px] font-bold" style={{ color: '#c7e0f4' }}>How to prepare for the labs</h3>
            {[
              'Set up a free Azure trial subscription (30-day trial, or Visual Studio subscription if you have one)',
              'Complete all Microsoft Learn sandbox exercises — do not just read the instructions',
              'Practice each common lab task type until you can complete it from memory without documentation',
              'Time yourself — lab tasks have a shared time budget with the rest of the exam',
              'Know both the Azure portal interface and Azure CLI/PowerShell approaches — the lab may not specify which',
              'Common lab task list: PIM role assignment, Key Vault creation, Conditional Access policy, custom RBAC role, DDoS protection plan, storage account network rules',
            ].map((item) => (
              <div key={item} className="flex items-start gap-2 text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                <span className="text-[#0078d4] shrink-0 mt-0.5">›</span>
                {item}
              </div>
            ))}
          </div>
        </section>

        {/* ── Section 9: Related Resources ── */}
        <section id="related-resources" className="mb-6">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Related Resources</h2>

          <div className="flex flex-col gap-3 mb-6">
            <ResourceLink
              title="Official AZ-500 certification page"
              url="https://learn.microsoft.com/en-us/credentials/certifications/azure-security-engineer/"
              source="Microsoft Learn"
            />
            <ResourceLink
              title="AZ-500 study guide (official)"
              url="https://learn.microsoft.com/en-us/credentials/certifications/resources/study-guides/az-500"
              source="Microsoft Learn"
            />
            <ResourceLink
              title="SC-200 Study Guide UK — nearest successor"
              url="/certifications/sc-200-study-guide-uk"
              source="AzureCalc.uk"
            />
            <ResourceLink
              title="Azure security documentation"
              url="https://learn.microsoft.com/en-us/azure/security/"
              source="Microsoft Learn"
            />
          </div>

          <p className="text-[13px] mb-3" style={{ color: 'var(--text-tertiary)' }}>
            Estimate your Azure security monitoring costs — Defender for Cloud and Microsoft
            Sentinel are key AZ-500 exam topics:
          </p>
          <div className="flex flex-col gap-3">
            <CalculatorEmbed service="log-analytics" />
            <CalculatorEmbed service="app-service" />
          </div>
        </section>
      </CertificationLayout>
    </>
  );
}
